/**
 * ERIC Chatbot v2: traduce una respuesta con `modo: "v2"` al modelo de mensaje
 * que usa el panel de ERIC.
 *
 * La v2 responde en un solo turno con texto, tablas y fuentes citadas (ver
 * vec-fleet-eric/docs/eric-chatbot-v2-plan.md). El backend la devuelve por
 * `/api/eric/consultar` cuando el tenant está en v2, así que el panel la
 * reconoce por el `modo` y no por el endpoint.
 *
 * La usa la respuesta en vivo (`data` de POST /eric/consultar), en EricAnalyst.
 * En la base, en cambio, el mensaje se guarda con el modo de la v1 (analyst,
 * chart o assistant) y con la forma de la v1 en `payload_extra`: al reabrir la
 * conversación se dibuja como uno de la v1, más lo que devuelve
 * `extrasGuardados` (tablas adicionales y fuentes).
 *
 * El primer resultado ocupa la tabla principal del mensaje (así funcionan
 * sin cambios copiar, CSV y XLSX); los demás van en `tablasAdicionales`. Si la
 * v2 graficó un resultado, es el primero y su `grafico` es la chart_spec del
 * modo chart de la v1: se dibuja con el mismo EricChart.
 *
 * Respuesta v2 → mensaje:
 *   { tipo, respuesta, resultados: [{ref, titulo, columnas, filas, total_filas, advertencia, grafico}],
 *     fuentes: [{tipo, id, titulo, url}], presupuesto_agotado }
 *     →
 *   { respuesta, columnas, filas, totalFilas, advertencia, chartSpec, metodologia,
 *     tablasAdicionales, fuentes, esAclaracion, presupuestoAgotado }
 */

export var MODO_V2 = 'v2';

function normalizarTabla(resultado) {
  return {
    titulo: resultado && resultado.titulo ? resultado.titulo : '',
    columnas: resultado && resultado.columnas ? resultado.columnas : [],
    filas: resultado && resultado.filas ? resultado.filas : [],
    totalFilas: resultado && resultado.total_filas ? resultado.total_filas : 0,
    advertencia: resultado && resultado.advertencia ? resultado.advertencia : null,
  };
}

function normalizarFuente(fuente) {
  return {
    tipo: fuente.tipo === 'corpus' ? 'corpus' : 'confluence',
    id: String(fuente.id),
    titulo: fuente.titulo ? fuente.titulo : '',
    url: fuente.url ? fuente.url : null,
  };
}

/**
 * Lo que la v2 guarda en `payload_extra` además de la forma de la v1. Vale para
 * cualquier mensaje del historial: uno de la v1 no trae estos campos y queda
 * con las listas vacías.
 *
 * @param {object} extra - payload_extra del mensaje guardado.
 * @returns {{ tablasAdicionales: Array, fuentes: Array }}
 */
export function extrasGuardados(extra) {
  var e = extra || {};
  var fuentes = Array.isArray(e.fuentes) ? e.fuentes : [];

  return {
    tablasAdicionales: Array.isArray(e.tablas_adicionales) ? e.tablas_adicionales.map(normalizarTabla) : [],
    fuentes: fuentes
      .filter(function(f) { return f && f.id != null; })
      .map(normalizarFuente),
  };
}

/**
 * @param {object} datos - `data` de la respuesta en vivo.
 * @returns {object}
 */
export function normalizarRespuestaV2(datos) {
  var d = datos || {};
  var resultados = Array.isArray(d.resultados) ? d.resultados : [];
  var primero = resultados.length > 0 ? resultados[0] : null;
  var principal = normalizarTabla(primero);
  var fuentes = Array.isArray(d.fuentes) ? d.fuentes : [];

  return {
    respuesta: d.respuesta ? d.respuesta : null,
    columnas: principal.columnas,
    filas: principal.filas,
    totalFilas: principal.totalFilas,
    advertencia: principal.advertencia,
    chartSpec: primero && primero.grafico ? primero.grafico : null,
    // «Cómo se calculó»: la metodología de la tabla principal, como en la v1.
    metodologia: primero && primero.metodologia ? primero.metodologia : null,
    tablasAdicionales: resultados.slice(1).map(normalizarTabla),
    fuentes: fuentes
      .filter(function(f) { return f && f.id != null; })
      .map(normalizarFuente),
    esAclaracion: d.tipo === 'aclaracion',
    presupuestoAgotado: !!d.presupuesto_agotado,
  };
}
